import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApi, ApiError, type StateListResponse } from '../api/client';
import type { FactorStateKind } from '../framework';
import { StateIcon } from './StateIcon';
import './StateAppearance.css';

/**
 * Admin editor for the four factor-state icons + colours at
 * /admin/states. Each state renders as a card with the current icon
 * preview, a colour picker, and an SVG upload control. Save persists
 * via PUT /api/v1/admin/states/{state_id}.
 *
 * Admin.appearance permission required (server-gated).
 */

const STATES: FactorStateKind[] = ['risk', 'change', 'protective', 'not-assessed'];

const DEFAULT_COLOURS: Record<FactorStateKind, string> = {
  risk: '#c13b2c',
  change: '#b8810e',
  protective: '#0e8e78',
  'not-assessed': '#6b7280',
};

type FormState = {
  colour: string;
  iconSvg: string | null;
  iconSvgDirty: boolean;
  uploading: boolean;
  saving: boolean;
  saved: boolean;
  error: string | null;
};

const emptyForm = (state: FactorStateKind): FormState => ({
  colour: DEFAULT_COLOURS[state],
  iconSvg: null,
  iconSvgDirty: false,
  uploading: false,
  saving: false,
  saved: false,
  error: null,
});

export function StateAppearance() {
  const { t } = useTranslation();
  const callApi = useApi();

  const [forms, setForms] = useState<Record<FactorStateKind, FormState>>(() => ({
    risk: emptyForm('risk'),
    change: emptyForm('change'),
    protective: emptyForm('protective'),
    'not-assessed': emptyForm('not-assessed'),
  }));
  const [loadState, setLoadState] = useState<
    | { kind: 'loading' }
    | { kind: 'ok' }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await callApi<StateListResponse>('/admin/states');
        if (cancelled) return;
        setForms((prev) => {
          const next = { ...prev };
          for (const stateId of STATES) {
            const row = data.rows.find((r) => r.state_id === stateId);
            next[stateId] = {
              ...emptyForm(stateId),
              colour: row?.colour ?? DEFAULT_COLOURS[stateId],
              iconSvg: row?.icon_svg ?? null,
            };
          }
          return next;
        });
        setLoadState({ kind: 'ok' });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setLoadState({ kind: 'ok' });
          return;
        }
        const message =
          err instanceof ApiError
            ? `${String(err.status)} — ${err.message}`
            : err instanceof Error
              ? err.message
              : 'Failed to load state settings';
        setLoadState({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateForm = (stateId: FactorStateKind, patch: Partial<FormState>) => {
    setForms((prev) => ({ ...prev, [stateId]: { ...prev[stateId], ...patch } }));
  };

  const handleSvgUpload = (stateId: FactorStateKind, file: File) => {
    if (!/\.svg$/i.test(file.name) && file.type !== 'image/svg+xml') {
      updateForm(stateId, { error: t('stateAppearance.errNotSvg') });
      return;
    }
    if (file.size > 100_000) {
      updateForm(stateId, { error: t('stateAppearance.errTooLarge') });
      return;
    }
    updateForm(stateId, { uploading: true, error: null });
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        updateForm(stateId, {
          iconSvg: result,
          iconSvgDirty: true,
          uploading: false,
          saved: false,
        });
      }
    };
    reader.onerror = () => {
      updateForm(stateId, {
        uploading: false,
        error: t('stateAppearance.errReadFile'),
      });
    };
    reader.readAsText(file);
  };

  const handleClearSvg = (stateId: FactorStateKind) => {
    updateForm(stateId, {
      iconSvg: null,
      iconSvgDirty: true,
      saved: false,
    });
  };

  const handleSave = async (stateId: FactorStateKind) => {
    const form = forms[stateId];
    updateForm(stateId, { saving: true, error: null });
    try {
      const body: Record<string, unknown> = { colour: form.colour };
      if (form.iconSvgDirty) {
        body.icon_svg = form.iconSvg ?? '';
      }
      await callApi(`/admin/states/${encodeURIComponent(stateId)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: { 'content-type': 'application/json' },
      });
      updateForm(stateId, {
        saving: false,
        saved: true,
        iconSvgDirty: false,
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${String(err.status)} — ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Save failed';
      updateForm(stateId, { saving: false, error: message });
    }
  };

  return (
    <article className="state-appearance">
      <header className="state-appearance__header">
        <h1 className="state-appearance__title">{t('stateAppearance.title')}</h1>
        <p className="state-appearance__subtitle">
          {t('stateAppearance.subtitle')}
        </p>
      </header>

      {loadState.kind === 'loading' && (
        <p className="state-appearance__status">{t('stateAppearance.loading')}</p>
      )}
      {loadState.kind === 'error' && (
        <p className="state-appearance__status state-appearance__status--error">
          {loadState.message}
        </p>
      )}

      {loadState.kind === 'ok' && (
        <div className="state-appearance__grid">
          {STATES.map((stateId) => {
            const form = forms[stateId];
            return (
              <section
                key={stateId}
                className="state-appearance-card"
                style={{ '--state-current': form.colour } as React.CSSProperties}
              >
                <header className="state-appearance-card__header">
                  <h2 className="state-appearance-card__heading">
                    {t(
                      `framework.state.${
                        stateId === 'not-assessed' ? 'notAssessed' : stateId
                      }.descriptor`,
                    )}
                  </h2>
                </header>

                <div className="state-appearance-card__preview">
                  <span
                    className="state-appearance-card__icon"
                    style={{ color: form.colour }}
                  >
                    <StateIcon state={stateId} customSvg={form.iconSvg} />
                  </span>
                </div>

                <div className="state-appearance-card__field">
                  <label htmlFor={`state-${stateId}-colour`}>
                    {t('stateAppearance.colourLabel')}
                  </label>
                  <div className="state-appearance-card__colour-row">
                    <input
                      id={`state-${stateId}-colour`}
                      type="color"
                      value={form.colour}
                      onChange={(e) =>
                        updateForm(stateId, {
                          colour: e.target.value,
                          saved: false,
                        })
                      }
                    />
                    <span className="state-appearance-card__colour-value">
                      {form.colour}
                    </span>
                  </div>
                </div>

                <div className="state-appearance-card__field">
                  <label>{t('stateAppearance.iconLabel')}</label>
                  <div className="state-appearance-card__icon-actions">
                    <label className="auth-button auth-button--secondary">
                      {form.uploading
                        ? t('stateAppearance.uploading')
                        : t('stateAppearance.chooseSvg')}
                      <input
                        type="file"
                        accept="image/svg+xml,.svg"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleSvgUpload(stateId, file);
                        }}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {form.iconSvg && (
                      <button
                        type="button"
                        className="auth-button auth-button--secondary"
                        onClick={() => handleClearSvg(stateId)}
                      >
                        {t('stateAppearance.resetIcon')}
                      </button>
                    )}
                  </div>
                </div>

                <aside className="state-appearance-card__note">
                  <strong>{t('stateAppearance.note.heading')}</strong>
                  <ul>
                    <li>{t('stateAppearance.note.currentColor')}</li>
                    <li>{t('stateAppearance.note.size')}</li>
                    <li>{t('stateAppearance.note.allowed')}</li>
                  </ul>
                </aside>

                <div className="state-appearance-card__actions">
                  <button
                    type="button"
                    className="auth-button auth-button--primary"
                    onClick={() => void handleSave(stateId)}
                    disabled={form.saving}
                  >
                    {form.saving
                      ? t('stateAppearance.saving')
                      : t('stateAppearance.save')}
                  </button>
                  {form.saved && (
                    <span className="state-appearance-card__saved">
                      {t('stateAppearance.saved')}
                    </span>
                  )}
                  {form.error && (
                    <span className="state-appearance-card__error">
                      {form.error}
                    </span>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </article>
  );
}

export default StateAppearance;
